import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Bugsnag from '@bugsnag/js'
import Modal from '@groovehq/internal-design-system/lib/components/Modal/Modal'
import Button from '@groovehq/internal-design-system/lib/components/Button/Button'
import { DRAWER_TYPE_BILLING_EXTERNAL_APPROVE } from 'ducks/drawers/types'
import { useDrawer } from 'ducks/drawers/hooks'
import { useBilling } from 'ducks/billing/hooks'
import debug from 'util/debug'
import { HIDE_ANNUAL_OFFER } from 'ducks/flags/flagNames'
import { doSetFlag, selectFlag } from 'ducks/flags'
import cash from 'assets/cash.png'
import { getFormattedPrice, percentageDecrease } from 'util/numbers'

import { styles } from './styles'

const AnnualOffer = ({ onExit, open }) => {
  const dispatch = useDispatch()
  const [disabled, setDisabled] = useState(false)
  const annualOfferHidden = useSelector(selectFlag)(HIDE_ANNUAL_OFFER)
  const { openDrawer: openBillingExternalApprove } = useDrawer({
    type: DRAWER_TYPE_BILLING_EXTERNAL_APPROVE,
  })

  const {
    updateSubscription,
    currentInbox: { plan: { planId: inboxPlanId } = {} },
    currentKb: { plan: { planId: kbPlanId } = {} },
    calculateEstimatedTotalBill,
  } = useBilling()

  const onClose = useCallback(
    () => {
      onExit()
      if (annualOfferHidden !== true) {
        dispatch(doSetFlag(HIDE_ANNUAL_OFFER))
      }
    },
    [onExit, dispatch, annualOfferHidden]
  )

  const handleUpgradeToAnnual = useCallback(
    async () => {
      setDisabled(true)

      const data = {
        billingCycle: 'annual',
        inboxPlanId,
        kbPlanId,
      }

      try {
        const response = await updateSubscription(data, {
          successMessage: 'Your account has been upgraded to annual',
        })

        const {
          updateSubscription: {
            pendingSubscription: { approvalUrl } = {},
          } = {},
        } =
          response || {}

        if (approvalUrl) {
          openBillingExternalApprove()
        }

        onClose()
      } catch (error) {
        onExit()
        debug(error)
        Bugsnag.notify(error)
      }
      setDisabled(false)
    },
    [
      openBillingExternalApprove,
      updateSubscription,
      inboxPlanId,
      kbPlanId,
      onClose,
      onExit,
    ]
  )

  const totalMonthlyBillForAYear = useMemo(
    () => calculateEstimatedTotalBill('monthly') * 12,
    [calculateEstimatedTotalBill]
  )

  const totalAnnualBill = useMemo(() => calculateEstimatedTotalBill('annual'), [
    calculateEstimatedTotalBill,
  ])

  const totalSavings = Math.max(0, totalMonthlyBillForAYear - totalAnnualBill)
  const savingsPercent = Math.max(
    0,
    Math.trunc(percentageDecrease(totalMonthlyBillForAYear, totalAnnualBill))
  )

  return (
    <Modal
      portal="#overlays"
      open={open}
      className="grui pb-14"
      css={styles.annualOffer}
      onClose={onClose}
    >
      <div css={styles.imgWrapper}>
        <img src={cash} alt="money" />
      </div>
      <div className="grui text-center px-17">
        <Modal.Title className="grui mt-16">
          Ready for some savings?
        </Modal.Title>
        <Modal.Description>
          Switch to an annual plan to{' '}
          <strong>save up to {savingsPercent}%</strong> on your Groove account!
        </Modal.Description>
        <strong css={styles.savingTitle}>Your savings per year</strong>
        <strong className="grui tilt-highlight" css={styles.saving}>
          <span>{getFormattedPrice(totalSavings, 'USD')}</span>
        </strong>
        <Modal.Description>
          When you move from a monthly to annual plan you’ll continue to get all
          the time saving and productivity enhancing benefits of Groove’s{' '}
          {app.t('Mailbox')} and Knowledge Base at a better rate.
        </Modal.Description>
        <Button
          type="info"
          size="xBig"
          className="grui mt-11 mb-5"
          onClick={handleUpgradeToAnnual}
          disabled={disabled}
          id="cta-annual-offer"
        >
          Upgrade me to annual
        </Button>
        <Modal.Description css={styles.note}>
          *By upgrading now, your credit card will be pro-rata billed
          immediately
        </Modal.Description>
      </div>
    </Modal>
  )
}

export default AnnualOffer
